import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';
import { CLIENT_ID, USER_POOL_ID } from '../__common/env';
import { useMainContext } from '../hooks/useMainContext';

export default function useUserSession() {
    const { viewManager } = useMainContext();
    const navigate = useNavigate();
    const poolData = {
        UserPoolId: USER_POOL_ID,
        ClientId: CLIENT_ID,
    };

    const getCurrentUserAttrValue = useCallback(() => {
        return new Promise((resolve, reject) => {
            const userPool = new CognitoUserPool(poolData);
            const cognitoUser = userPool.getCurrentUser();
            if (!cognitoUser) {
                reject('No user found');
                return;
            }

            cognitoUser.getSession((err, session) => {
                if (err || !session) {
                    reject('Session error');
                    return;
                }

                cognitoUser.getUserAttributes((err, attributes) => {
                    if (err) {
                        reject(err);
                        return;
                    }
                    const attrMap = {};
                    attributes.forEach((attr) => {
                        attrMap[attr.getName()] = attr.getValue();
                    });

                    resolve(attrMap);
                });
            });
        });
    }, []);

    const getCognitoSession = useCallback(() => {
        const userPool = new CognitoUserPool(poolData);
        const cognitoUser = userPool.getCurrentUser();

        if (!cognitoUser) {
            viewManager.setIsLoggedIn(false);
            viewManager.setUserData(null);
            return Promise.resolve({ isLoggedIn: false });
        }

        return new Promise((resolve, reject) => {
            cognitoUser.getSession(async (err, session) => {
                if (err || !session?.isValid()) {
                    viewManager.setIsLoggedIn(false);
                    resolve({ isLoggedIn: false });
                    return;
                }

                try {
                    const attrs = await getCurrentUserAttrValue();
                    const userAttrs = {
                        name: attrs['name'],
                        firstName: attrs['given_name'],
                        lastName: attrs['family_name'],
                        email: attrs['email'],
                        id: attrs['sub'],
                        authProvider: 'cognito',
                    };
                    resolve({
                        session,
                        user: userAttrs,
                        isLoggedIn: true,
                    });
                } catch (error) {
                    viewManager.setIsLoggedIn(false);
                    reject(error);
                }
            });
        });
    }, [getCurrentUserAttrValue]);

    const logOutUser = useCallback(() => {
        const userPool = new CognitoUserPool(poolData);
        const cognitoUser = userPool.getCurrentUser();

        if (cognitoUser) {
            cognitoUser.signOut();
            return Promise.resolve('Sign out successful');
        } else {
            return Promise.resolve('No user found');
        }
    }, []);

    const checkAuth = useCallback(async () => {
        viewManager.setIsLoader(true);
        try {
            const sessionInfo = await getCognitoSession();
            if (sessionInfo.isLoggedIn) {
                viewManager.setUserData(sessionInfo.user);
                viewManager.setIsLoggedIn(true);
            }
        } catch {
            viewManager.setIsLoggedIn(false);
            viewManager.setUserData(null);
        } finally {
            viewManager.setIsLoader(false);
        }
    }, [getCognitoSession, navigate]);

    return { getCognitoSession, logOutUser, checkAuth };
}
